import styles from './ForYou.module.scss';
import { Icon, TicketIcon, Typography } from '@eventbrite/marmalade';
import { FormattedEvent } from '@eventbrite/consumer-search.event-renderer';
import { EventCardsBucket } from '../EventCardsBucket/EventCardsBucket';
import { LoadingBucket } from '../LoadingBucket';
import { DoLoginEmptyState } from './DoLoginEmptyState';
import { NoEventsEmptyState } from './NoEventsEmptyState';
import { useRecommendations } from './useRecommendations';
import { usePromotedEvents } from './usePromotedEvents';
import { useSavedEventIds } from '@/common/useSavedEventIds';
import { useClientAuth } from '@/common/useUserPublicId';
import { useIsomorphicLayoutEffect } from '@/common/useIsomorphicLayoutEffect';
import { useIsForYouPromotedEventsEnabled } from '@/bsp/config';
import { PROMOTED_EVENTS_RANKS } from '@/constants/promoted';
import { Location } from '@/types';
import { mergePromotedEvents } from '@/shared/utils/mergePromotedEvents';
import { useMemo } from 'react';

interface ForYouProps {
    /** Where the ad auction should be run — the same location the rest of the homepage feed is browsing. */
    location: Location;
    /**
     * Whether the page has finished trying to resolve the browsing location, successfully or not.
     *
     * The auction cannot run without a place, so painting cards before this settles means painting them ad-free and
     * then yanking them back for a skeleton the moment the location arrives and the auction starts.
     */
    isLocationSettled?: boolean;
    /**
     * Reports the flat list of events the tab actually renders, in render order.
     *
     * BrowseContainer ranks impressions and clicks against this list, so promoted cards have to be in it, in slot order.
     */
    onRenderedEventsChange?: (events: FormattedEvent[]) => void;
}

export const ForYou: React.FC<ForYouProps> = ({
    location,
    isLocationSettled = true,
    onRenderedEventsChange,
}) => {
    const { authState, publicUserId } = useClientAuth();
    const isAuthenticated = authState === 'authenticated';
    const { isEnabled: isPromotedEventsEnabled } = useIsForYouPromotedEventsEnabled();

    // Waits for isLocationSettled, not for a resolved place: location.placeId lands in the very same render that
    // flips isLocationSettled true, so this fires its first request already keyed on the real placeId instead of
    // firing once under a null-place key and immediately again once geo resolves. Unlike usePromotedEvents below,
    // this does not require a resolved place to be worth running — the endpoint falls back to IP-based location
    // server-side — so gating it on Boolean(location.placeId || location.isOnline) like the auction would wrongly
    // block recommendations forever whenever the client-side geo lookup itself fails.
    const { data, isFetching } = useRecommendations({
        location,
        enabled: isAuthenticated && isLocationSettled,
    });
    const { data: promotedEvents, isFetching: isFetchingPromotedEvents } = usePromotedEvents({
        location,
        // Orion kill switch — see useIsForYouPromotedEventsEnabled — lets the auction be turned off by segment
        // without a deploy if it turns out to cannibalize organic ticket orders.
        enabled: isAuthenticated && isPromotedEventsEnabled,
    });

    // Ads ride in the first bucket only: PROMOTED_EVENTS_RANKS are positions within a single visible list,
    // and the first bucket is the one every For-you visitor lands on.
    const buckets = useMemo(() => {
        const recommendationBuckets = data ?? [];
        const promoted = isPromotedEventsEnabled ? (promotedEvents ?? []) : [];

        if (!recommendationBuckets.length || !promoted.length) {
            return recommendationBuckets;
        }

        // mergePromotedEvents only dedupes the array it's given, i.e. the first bucket. An auction winner that also
        // appears in a later bucket (e.g. "Trending Now") needs its organic copy dropped there too, or it renders
        // twice: once as the paid card, once as organic.
        const promotedIds = new Set(promoted.map((event) => event.id));
        const [firstBucket, ...restBuckets] = recommendationBuckets;

        return [
            {
                ...firstBucket,
                events: mergePromotedEvents(
                    firstBucket.events,
                    promoted,
                    PROMOTED_EVENTS_RANKS,
                ) as FormattedEvent[],
            },
            // A bucket can lose every event to the dedupe above when its only events were also served as ads.
            // Rendering it anyway would show a section header — e.g. "Trending Now" — over an empty list.
            ...restBuckets
                .map((bucket) => ({
                    ...bucket,
                    events: bucket.events.filter((event) => !promotedIds.has(event.id)),
                }))
                .filter((bucket) => bucket.events.length > 0),
        ];
    }, [data, isPromotedEventsEnabled, promotedEvents]);

    const eventIds = useMemo(
        () => buckets.flatMap((bucket) => bucket.events.map((event) => event.id)),
        [buckets],
    );
    const { savedEventIds, isSaveStateReady } = useSavedEventIds(
        publicUserId,
        eventIds,
        isAuthenticated && buckets.length > 0,
    );
    const personalizedBuckets = useMemo(() => {
        if (!isAuthenticated || !isSaveStateReady) {
            return buckets;
        }

        return buckets.map((bucket) => ({
            ...bucket,
            events: bucket.events.map((event) =>
                savedEventIds.has(event.id)
                    ? { ...event, savedByYou: true }
                    : { ...event, savedByYou: false },
            ),
        }));
    }, [buckets, isAuthenticated, isSaveStateReady, savedEventIds]);

    const renderedEvents = useMemo(
        () => personalizedBuckets.flatMap((bucket) => bucket.events),
        [personalizedBuckets],
    );

    // Reported before paint, not after.
    // Impressions fire from an IntersectionObserver, which the browser can only deliver once these cards have been
    // laid out and painted; a passive effect would let that happen while the parent still holds the previous list.
    // The tracker dedupes impressions by (id, placementId, action) for the life of the page, so a card that reports
    // rank 0 once reports it forever — the correct impression that follows is discarded as a duplicate.
    useIsomorphicLayoutEffect(() => {
        onRenderedEventsChange?.(renderedEvents);
    }, [onRenderedEventsChange, renderedEvents]);

    // Hold until location and recommendations have both settled. Recommendations decide whether there is anything
    // to show at all, and until they settle we don't know whether the auction's result could ever matter.
    //
    // `!data && isFetching`, not bare `isFetching`: useRecommendations leaves refetchOnWindowFocus/refetchOnReconnect
    // on, so a background refetch on an already-populated feed sets isFetching true too. Gating on isFetching alone
    // would unmount the live feed — ads included — for a skeleton on every tab refocus, which is exactly the kind
    // of impression-attribution churn the layout-effect reporting above exists to avoid.
    if (authState === 'unknown' || !isLocationSettled || (!data && isFetching)) {
        return <LoadingBucket />;
    }

    if (!isAuthenticated) {
        return <DoLoginEmptyState />;
    }

    // No `isError` here: React Query keeps the last successful `data` around across a failed background refetch
    // (window focus, reconnect) rather than clearing it, so `data` populated + `isError` true means "still showing
    // slightly stale good data," not "nothing to show." `!data` already covers the case isError is meant to catch —
    // a first load that never got a result — without wiping an already-visible feed on a transient refetch failure.
    if (!data || data.length === 0) {
        return <NoEventsEmptyState />;
    }

    // Recommendations exist, so now hold for the auction too. Injecting ads after the organic cards paint would
    // shove every card below them down and log impressions against positions the user never saw. A failed or
    // timed-out auction still renders ad-free — usePromotedEvents just resolves to no data.
    if (isFetchingPromotedEvents) {
        return <LoadingBucket />;
    }

    return (
        <>
            {personalizedBuckets.map((bucket) => (
                <div key={bucket.name} className={styles.forYouBucket}>
                    <div className={styles.bucketHeader}>
                        <Icon size="xlarge">
                            <TicketIcon />
                        </Icon>
                        <Typography as="h3">{bucket.name}</Typography>
                    </div>
                    <EventCardsBucket
                        label={bucket.name}
                        affCode="ebdssbehomepeforyou"
                        events={bucket.events}
                        isAuthenticated={isAuthenticated}
                        showSaveAction={isSaveStateReady}
                        showShareAction={true}
                    />
                </div>
            ))}
        </>
    );
};
